import React, { useState } from 'react';
import { Gift, CheckCircle } from 'lucide-react';
import { api } from '../../services/apiService';

import { RubyGemMark } from '../RubyGemMark';
/**
 * APPROVE AN ADDITIONAL-LOCATION REQUEST (2026-08-26 Plan & Packages spec).
 *
 * The admin no longer picks a raw Stripe price. The location approves at the
 * tenant's DEFAULT additional-location price — the deal's
 * `additionalLocationPriceCents` (every deal defines it now), falling back to
 * the billing catalog's `addl_location` SKU — and the admin's only levers are
 * "comp it free" or an attributable discount (percent or dollars, with a
 * reason). The server resolves the default and creates/reuses the Stripe
 * price for the net; see location-billing-service `admin/approve.ts`.
 *
 * Used from BOTH approve surfaces (SuperAdminLocationDetail's decision bar
 * and TenantProfile's Plan & Packages tab) so they cannot drift.
 */
interface ApproveLocationFormProps {
  tenantId: string;
  requestId: string;
  locationName?: string;
  /** The deal's location terms when the caller has them — display only. */
  dealHint?: { additionalLocationPriceCents?: number; dealCode?: string } | null;
  onDone: (message: string) => void;
  onCancel: () => void;
}

const ApproveLocationForm: React.FC<ApproveLocationFormProps> = ({
  tenantId, requestId, locationName, dealHint, onDone, onCancel,
}) => {
  const [mode, setMode] = useState<'default' | 'free'>('default');
  const [discountKind, setDiscountKind] = useState<'none' | 'percent' | 'amount'>('none');
  const [discountValue, setDiscountValue] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const defaultHint = typeof dealHint?.additionalLocationPriceCents === 'number'
    ? `$${(dealHint.additionalLocationPriceCents / 100).toFixed(2)}/mo${dealHint.dealCode ? ` (deal ${dealHint.dealCode})` : ''}`
    : null;

  const handleApprove = async () => {
    setError(null);
    let payload: any = { tenantId };
    if (mode === 'free') {
      payload.isFree = true;
    } else if (discountKind !== 'none') {
      const value = Number(discountValue);
      if (!discountValue || !Number.isFinite(value) || value <= 0 || (discountKind === 'percent' && value > 100)) {
        setError(discountKind === 'percent' ? 'Enter a percent between 0 and 100.' : 'Enter a discount amount in dollars.');
        return;
      }
      payload.discount = {
        ...(discountKind === 'percent' ? { percentOff: value } : { amountOffCents: Math.round(value * 100) }),
        ...(discountReason.trim() ? { reason: discountReason.trim() } : {}),
      };
    }
    setSubmitting(true);
    try {
      await api.locationBilling.approveRequest(requestId, payload);
      onDone(mode === 'free'
        ? `${locationName || 'Location'} approved free — active immediately.`
        : `${locationName || 'Location'} approved — tenant will be asked to complete payment.`);
    } catch (err: any) {
      setError(err?.message || 'Failed to approve request');
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setMode('default')}
          className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border text-left ${
            mode === 'default' ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-white border-stone-300 text-stone-600 hover:bg-stone-50'
          }`}
        >
          <span className="flex items-center gap-1.5 font-semibold"><CheckCircle size={13} /> Default price</span>
          <span className="block mt-0.5 text-[11px] font-normal">
            {defaultHint ? `Bills at ${defaultHint}` : "The tenant's deal / catalog default"} — discount optional
          </span>
        </button>
        <button
          type="button"
          onClick={() => setMode('free')}
          className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border text-left ${
            mode === 'free' ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-white border-stone-300 text-stone-600 hover:bg-stone-50'
          }`}
        >
          <span className="flex items-center gap-1.5 font-semibold"><Gift size={13} /> Comp it free</span>
          <span className="block mt-0.5 text-[11px] font-normal">No billing — active immediately</span>
        </button>
      </div>

      {mode === 'default' && (
        <>
          <div className="flex gap-2 flex-wrap">
            {([['none', 'No discount'], ['percent', '% off'], ['amount', '$ off']] as const).map(([kind, label]) => (
              <button
                key={kind}
                type="button"
                onClick={() => setDiscountKind(kind)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                  discountKind === kind
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {discountKind !== 'none' && (
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                min="0"
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                placeholder={discountKind === 'percent' ? 'Percent off (e.g., 25)' : 'Dollars off (e.g., 10.00)'}
                className="w-full bg-white border border-stone-300 rounded-lg px-3 py-2 text-stone-900 text-sm"
              />
              <input
                type="text"
                value={discountReason}
                onChange={(e) => setDiscountReason(e.target.value)}
                placeholder="Reason (e.g., founding store)"
                className="w-full bg-white border border-stone-300 rounded-lg px-3 py-2 text-stone-900 text-sm"
              />
            </div>
          )}
        </>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}

      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="px-4 py-2 text-sm text-stone-600 hover:text-stone-800"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleApprove}
          disabled={submitting || (mode === 'default' && discountKind !== 'none' && !discountValue)}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
        >
          {submitting ? <RubyGemMark size={14} drawing color="currentColor" /> : <CheckCircle size={14} />}
          {mode === 'free' ? 'Approve Free' : 'Approve'}
        </button>
      </div>
    </div>
  );
};

export default ApproveLocationForm;
